<template>
  <section v-if="activeLivingOption" class="row">
    <card>
      <language-selector />
      <div class="d-flex flex-wrap">
        <div class="col-12 mb-4">
          <div class="row">
            <div class="col-12">
              <h3>Editing {{ activeLivingOption.label }}</h3>
              <button class="btn btn-sm btn-primary" @click="viewAll">
                View all living options
              </button>
              <button
                class="btn btn-sm btn-primary ml-1"
                @click="saveLivingOption(activeLivingOption)"
              >
                Save
              </button>
            </div>
          </div>
        </div>

        <div class="col-6 mb-4">
          <div class="row">
            <div class="col-6 mb-4">
              Label
              <input
                type="text"
                class="form-control form-control-lg"
                v-model="activeLivingOption.label"
                placeholder="i.e. Juman One"
              />
            </div>
            <div class="col-6 mb-4">
              Display Label
              <localised-field-indicator />
              <input
                type="text"
                :dir="activeLanguage.rtl ? 'rtl' : 'ltr'"
                class="form-control form-control-lg"
                v-model="activeLivingOption.displayName[activeLanguage.code]"
                placeholder="i.e. Juman One"
              />
            </div>
            <div class="col-6 mb-4">
              Access PIN
              <input
                type="text"
                inputmode="numeric"
                class="form-control form-control-lg"
                v-model.trim="activeLivingOption.accessCode"
                placeholder="Optional"
              />
            </div>

            <div class="col-12 mb-4">
              Sidebar Description
              <localised-field-indicator />
              <ck-editor
                id="livingOptionDescription"
                v-model="activeLivingOption.description[activeLanguage.code]"
                :dir="activeLanguage.rtl ? 'rtl' : 'ltr'"
                :key="activeLanguage.code"
                :language="activeLanguage.code"
              ></ck-editor>
            </div>
            <div class="col-12 mb-4">
              <label>
                Background Image
                <localised-field-indicator />
              </label>
              <input-asset
                v-model="
                  activeLivingOption.backgroundImage[activeLanguage.code]
                "
                :multiple="false"
              ></input-asset>
            </div>
            <div class="form-group">
              <label>Background Video Settings</label>
              <div class="d-flex">
                <input
                  type="checkbox"
                  v-model="activeLivingOption.backgroundVideoSettings.loop"
                />
                <span class="ml-2">Loop Video</span>
              </div>
              <div class="d-flex">
                <input
                  type="checkbox"
                  v-model="
                    activeLivingOption.backgroundVideoSettings.showControls
                  "
                />
                <span class="ml-2">
                  Show Controls
                  <InfoBox
                    >Show the video player controls (pause, mute).</InfoBox
                  >
                </span>
              </div>
              <div class="d-flex">
                <input
                  type="checkbox"
                  v-model="activeLivingOption.backgroundVideoSettings.delayTags"
                />
                <span class="ml-2">
                  Delay Tags
                  <InfoBox>
                    Delay animating tags in until after video play back has
                    finished.
                  </InfoBox>
                </span>
              </div>
              <div class="d-flex">
                <input
                  type="checkbox"
                  v-model="
                    activeLivingOption.backgroundVideoSettings.startMuted
                  "
                />
                <span class="ml-2">
                  Start Muted
                  <InfoBox>
                    Start the video with the sound muted. Can be unmuted if
                    "Show Controls" is checked.
                  </InfoBox>
                </span>
              </div>
            </div>
            <div class="col-12 mb-4">
              Living Option Logo
              <input-asset v-model="activeLivingOption.logo"></input-asset>
            </div>
            <div class="col-12 mb-4">
              Card Background Image
              <input-asset
                v-model="activeLivingOption.cardBackgroundImage"
              ></input-asset>
            </div>
            <div class="col-12 mb-4">
              Sidebar Minimap Image
              <input-asset
                v-model="activeLivingOption.sidebarMinimap"
              ></input-asset>
            </div>

            <div class="col-12 mb-2">
              <hr />
            </div>

            <div class="col-12 mb-4">Visibility Settings</div>

            <div class="col-6 col-lg-4 mb-4">
              <div class="d-flex align-items-center flex-column">
                <toggle-button
                  v-model="activeLivingOption.showOnMap"
                  color="#28a745"
                  :sync="true"
                  :labels="false"
                  :width="50"
                  :height="30"
                  style="margin: 3px 0 0"
                />
                <span class="text-center">Show Tag and Polygon</span>
              </div>
            </div>
            <div class="col-6 col-lg-4 mb-4">
              <div class="d-flex align-items-center flex-column">
                <toggle-button
                  v-model="activeLivingOption.showNavigationSlide"
                  color="#28a745"
                  :sync="true"
                  :labels="false"
                  :width="50"
                  :height="30"
                  style="margin: 3px 0 0"
                />
                <span class="text-center">Show Navigation Slide</span>
              </div>
            </div>
            <div class="col-6 col-lg-4 mb-4">
              <div class="d-flex align-items-center flex-column">
                <toggle-button
                  v-model="activeLivingOption.showOnWebfeed"
                  color="#28a745"
                  :sync="true"
                  :labels="false"
                  :width="50"
                  :height="30"
                  style="margin: 3px 0 0"
                />
                <span class="text-center">Show on Webfeed</span>
              </div>
            </div>
            <div class="col-12 mb-2">
              <hr />
            </div>

            <div class="col-12 mb-4">Misc Settings</div>
            <div class="col-6 col-lg-4 mb-4">
              <div class="d-flex align-items-center flex-column">
                <toggle-button
                  v-model="activeLivingOption.isSoldOut"
                  color="#28a745"
                  :sync="true"
                  :labels="false"
                  :width="50"
                  :height="30"
                  style="margin: 3px 0 0"
                />
                <span class="text-center">Sold Out Override</span>
              </div>
            </div>

            <div class="col-6 col-lg-4 mb-4">
              <div class="d-flex align-items-center flex-column">
                <toggle-button
                  v-model="activeLivingOption.isFilterHidden"
                  color="#28a745"
                  :sync="true"
                  :labels="false"
                  :width="50"
                  :height="30"
                  style="margin: 3px 0 0"
                />
                <span class="text-center">Is Filter Slideout Hidden</span>
              </div>
            </div>
          </div>
        </div>

        <div class="col-6 mb-4">
          <div class="row">
            <div class="col-12 mb-4">
              Associated Subsections
              <input-multi-select
                v-model="activeLivingOption.subsectionIds"
                :options="subsectionOptions"
              ></input-multi-select>
            </div>
            <div class="col-12 mb-4">
              Videos (displayed on property favourites webpage)
              <input-asset
                v-model="activeLivingOption.videos"
                :multiple="true"
              ></input-asset>
            </div>
            <div class="col-12 mb-4">
              Brochure (displayed on property favourites webpage)
              <input-asset v-model="activeLivingOption.brochure"></input-asset>
            </div>

            <div class="col-12 mb-4">
              <fieldset class="form-group">
                <InputTransitionVideos
                  v-model="activeLivingOption.pageTransitionVideos"
                ></InputTransitionVideos>
              </fieldset>
            </div>
          </div>
        </div>

        <div class="col-12 mb-4 card-update-tags">
          <div class="title-row">
            <h4 class="card-title mb-0">Edit Canvas</h4>

            <div class="info-icon-wrapper">
              <div class="edit-tags-info">
                <InfoBox>
                  <div class="edit-tags-info__section">
                    <div class="edit-tags-info__label">Basics</div>
                    <ul>
                      <li>
                        Click <strong>Add Tag</strong> to create a new tag.
                      </li>
                      <li>Drag tags to reposition them on the map.</li>
                      <li>
                        Click a tag to select it;
                        <strong>double-click</strong> to open the Edit Menu (pen
                        icon) or click the icon itself.
                      </li>
                    </ul>
                  </div>

                  <div class="edit-tags-info__section">
                    <div class="edit-tags-info__label">Toolbar</div>
                    <ul>
                      <li>
                        Use the left toolbar for <strong>Zoom</strong>,
                        <strong>Undo / Redo</strong> and
                        <strong>Alignment</strong> tools.
                      </li>
                      <li>Selected tags can be aligned or moved together.</li>
                    </ul>
                  </div>

                  <div class="edit-tags-info__section">
                    <div class="edit-tags-info__label">Shortcuts</div>
                    <ul>
                      <li>Hold <strong>Shift</strong> to multi-select tags.</li>
                      <li>
                        Hold <strong>Shift</strong> when editing polygons to
                        toggle '+' icon used for adding vertices.
                      </li>
                      <li>
                        <strong>Double-Click</strong> a vertex/point on a
                        polygon to remove it.
                      </li>
                      <li>
                        <strong>Shift + Double-Click</strong> a tag to select
                        all tags of that type.
                      </li>
                      <li>
                        Use <strong>Shift +</strong> to zoom in and
                        <strong>Shift -</strong> to zoom out.
                        <strong>Shift 0</strong> to reset to default view.
                      </li>
                      <li>
                        Press <strong>Delete</strong> or
                        <strong>Backspace</strong> to remove a selected
                        tag/element.
                      </li>
                      <li>
                        Press <strong>Esc</strong> to clear selection or exit
                        edit mode.
                      </li>
                      <li>
                        Use <strong>Arrow Keys</strong> to adjust position for
                        'Pixel Perfect' placement.
                      </li>
                    </ul>
                  </div>
                </InfoBox>
              </div>
            </div>
          </div>

          <button class="btn btn-primary my-2 mr-2" @click="addLivingOptionTag">
            Add Tag
          </button>
          <draggable-elements
            :tags="activeLivingOption.tags"
            :subsection-ids="activeLivingOption.subsectionIds"
            :background-image="
              activeLivingOption.backgroundImage[activeLanguage.code]
            "
          />
        </div>
      </div>
    </card>
  </section>
</template>
<script>
import { includes, filter } from "lodash";
import { mapGetters } from "vuex";
import { FETCH_CONFIG_ITEMS, CONFIG_ITEMS_EDIT } from "@/store/actions.type";
import CkEditor from "@/components/CKEditor";
import DotDropper from "@/components/DotDropper";
import InputAsset from "@/components/InputAsset";
import InputMultiSelect from "@/components/InputMultiSelect";
import LivingOptionTags from "@/components/LivingOptionTags";
import LivingOptionMarkers from "@/components/LivingOptionMarkers";
import InfoBox from "@/components/InfoBox";
import DraggableElements from "@/components/DraggableElements";
import InputTransitionVideos from "@/components/InputTransitionVideos";

export default {
  components: {
    CkEditor,
    DotDropper,
    InputAsset,
    InputMultiSelect,
    LivingOptionTags,
    LivingOptionMarkers,
    InfoBox,
    DraggableElements,
    InputTransitionVideos,
  },

  data() {
    return {
      configItems: [],
      liveHereSidebarMinimap: "",
      liveHereDescription: {
        en: "",
      },
      liveHereHeading: {
        en: "",
      },
      liveHereBackground: "",
      liveHereBackgroundFallback: "",
      livingOptionsConfigItemsKeys: [
        "liveHereSidebarMinimap",
        "liveHereHeading",
        "liveHereDescription",
        "liveHereBackground",
        "liveHereBackgroundFallback",
      ],

      activeLivingOptionId: null,
      livingOptionsFilterText: "",
      filterActiveStates: [],

      pagesToTransition: [],
    };
  },

  computed: {
    ...mapGetters(["activeLanguage", "livingOptions", "subsections"]),

    activeLivingOption() {
      return this.livingOptions.find(
        (livingOption) => livingOption._id === this.activeLivingOptionId
      );
    },

    subsectionOptions() {
      let subsectionOptions = [];
      this.subsections.forEach((subsection) => {
        const isNotUsedLivingOption =
          subsection.parent.id === null ||
          subsection.parent.id === this.activeLivingOption._id; // is not being used by a different living option
        const isNotUsedSubsection = subsection.parent.type !== "subsection"; // is not being used by a subsection

        if (isNotUsedLivingOption && isNotUsedSubsection) {
          subsectionOptions.push({
            label: subsection.label,
            value: subsection._id,
          });
        }
      });
      return subsectionOptions;
    },

    liveOptionConfigTags() {
      return this.configItems.find(
        (item) => item.key === "liveHereMasterplanTags"
      );
    },

    filteredLivingOptions() {
      let filtered = filter(this.livingOptions, (item) => {
        return item.label
          .toLowerCase()
          .includes(this.livingOptionsFilterText.toLowerCase());
      });

      if (this.filterActiveStates.length) {
        filtered = filter(filtered, (item) => {
          return (
            (this.filterActiveStates.includes("Active") && item.active) ||
            (this.filterActiveStates.includes("Inactive") && !item.active)
          );
        });
      }

      return filtered;
    },
  },

  created() {
    this.$store.dispatch("FETCH_ASSETS");
    this.$store.dispatch("FETCH_LIVING_OPTIONS");
    this.$store.dispatch("FETCH_SUBSECTIONS");
    this.setData();
  },

  watch: {
    activeLivingOption: {
      immediate: true,
      handler(val) {
        if (!val) return;

        // ensure field exists
        if (
          !val.pageTransitionVideos ||
          typeof val.pageTransitionVideos !== "object"
        ) {
          this.$set(val, "pageTransitionVideos", {});
        }

        // remove legacy default key if it exists
        if (val.pageTransitionVideos && "default" in val.pageTransitionVideos) {
          const copy = { ...val.pageTransitionVideos };
          delete copy.default;
          this.$set(val, "pageTransitionVideos", copy);
        }
      },
    },
  },

  mounted() {
    this.activeLivingOptionId = this.$route.params.id;
  },

  methods: {
    viewAll() {
      this.$router.push("/live/masterplan");
    },

    saveConfig() {
      this.configItems.forEach((configItem) => {
        this.livingOptionsConfigItemsKeys.forEach((key) => {
          if (key === configItem.key) {
            configItem.value = this[key];
          }
        });
      });
      this.$store.dispatch(CONFIG_ITEMS_EDIT, this.configItems);
    },

    saveLivingOptions() {
      this.saveConfig();
      this.livingOptions.forEach((livingOption) => {
        if (livingOption.active) {
          this.$store.dispatch("SAVE_LIVING_OPTION", livingOption);
        }
      });
    },

    saveLivingOption(livingOption) {
      this.$store.dispatch("SAVE_LIVING_OPTION", livingOption);

      // reset parent property on removed subsections
      const matchedSubsections = this.subsections.filter(
        (subsection) => subsection.parent.id === livingOption._id
      );
      const removedSubsectionsIds = matchedSubsections
        .filter((subsection) => {
          if (!includes(livingOption.subsectionIds, subsection._id)) {
            return true;
          }
        })
        .map((subsection) => subsection._id);

      removedSubsectionsIds.forEach((subsectionId) => {
        const subsection = this.subsections.find(
          (subsection) => subsectionId === subsection._id
        );
        const subsectionUpdate = {
          _id: subsection._id,
          marker: subsection.marker,
          markers: subsection.markers,
          parent: {
            id: null,
            type: "",
          },
        };

        this.$store.dispatch("SAVE_SUBSECTION", subsectionUpdate);
      });

      // save subsections
      livingOption.subsectionIds.forEach((subsectionId) => {
        const subsection = this.subsections.find(
          (subsection) => subsectionId === subsection._id
        );

        const subsectionUpdate = {
          _id: subsection._id,
          marker: subsection.marker,
          markers: subsection.markers,
          polygon: subsection.polygon,
          polygons: subsection.polygons,
          parent: {
            id: livingOption._id,
            type: "livingOption",
          },
        };

        this.$store.dispatch("SAVE_SUBSECTION", subsectionUpdate);
      });

      this.viewAll();
    },

    async setData() {
      // Get the config items from the API
      this.configItems = await this.$store.dispatch(FETCH_CONFIG_ITEMS);

      // Reduce to just the live config items
      const liveConfigItems = filter(this.configItems, (item) => {
        return this.livingOptionsConfigItemsKeys.includes(item.key);
      });

      // Map the live items to the component data
      liveConfigItems.forEach((item) => {
        this[item.key] = item.value;
      });
    },

    addMasterplanTag() {
      this.$store.dispatch("GET_TAG_SCHEMA").then((res) => {
        this.liveOptionConfigTags.value.push(res.data);
      });
    },

    addLivingOptionTag() {
      this.$store.dispatch("GET_TAG_SCHEMA").then((res) => {
        this.activeLivingOption.tags.push(res.data);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.card-update-tags {
  position: relative;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.info-icon-wrapper {
  display: flex;
  align-items: center;
  transform: translateY(3px);
  z-index: 5000;
}

.edit-tags-info {
  max-width: 380px;

  &__section {
    &:not(:last-child) {
      margin-bottom: 10px;
      padding-bottom: 8px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }

    ul {
      margin: 4px 0 0;
      padding-left: 18px;
      font-size: 13px;
      line-height: 1.45;
    }
  }

  &__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #7a7a85;
    font-weight: 600;
  }
}
</style>
